import { type ClassValue, clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';
import apiClient from '@/api/client';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/** Format integer cents as RM currency string, e.g. 10050 → "RM 100.50" */
export function formatCurrency(cents: number): string {
  return `RM ${(cents / 100).toLocaleString('en-MY', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

const API_ERROR_MESSAGES: Record<string, string> = {
  RESOURCE_NOT_FOUND: 'Record not found.',
  OPERATION_NOT_ALLOWED: 'This action is not allowed at this stage.',
  PRECONDITION_FAILED: 'A required condition was not met.',
  VALIDATION_ERROR: 'Please check the form and try again.',
  FORBIDDEN: 'You do not have permission to do this.',
  DUPLICATE_RESOURCE: 'A record with these details already exists.',
  INVALID_CREDENTIALS: 'Invalid username or password.',
  ACCOUNT_LOCKED: 'Account is locked. Please try again later.',
  OVERPAYMENT: 'Payment amount exceeds the balance due.',
  ALREADY_REFUNDED: 'This payment has already been refunded.',
  STATE_INVALID_TRANSITION: 'This status change is not allowed.',
};

// PDF-download errors that deserve a curated (bilingual) message in place of
// the backend's raw text. Seen by staff (CustomerDetailPage, DocumentsCard) AND
// portal customers, so MS-first.
const PDF_ERROR_OVERRIDES: Record<string, string> = {
  // Remote handover signing: the handover PDF is refused until the customer's
  // /sign signature is back-filled.
  REMOTE_SIGNATURE_PENDING:
    'Menunggu tandatangan pelanggan melalui pautan WhatsApp - borang serah tersedia selepas ditandatangani. / Waiting for the customer signature via the WhatsApp link - the handover form is available once signed.',
};

/** Fetch a PDF from an authenticated API path and trigger a download */
export async function openPdf(apiPath: string): Promise<void> {
  try {
    const res = await apiClient.get<Blob>(apiPath, { responseType: 'blob' });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement('a');
    a.href = url;
    a.download = apiPath.split('/').pop() + '.pdf';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 10000);
  } catch (err: unknown) {
    const { showErrorToast } = await import('@/lib/toast');
    let message = 'Failed to download document. Please try again.';
    try {
      const blob = (err as any)?.response?.data;
      if (blob instanceof Blob) {
        const text = await blob.text();
        const json = JSON.parse(text);
        // Override only codes with a curated bilingual message (e.g.
        // REMOTE_SIGNATURE_PENDING on a deferred handover PDF — every surface
        // that opens these PDFs gets the proper explanation). Other codes keep
        // the backend's specific message, which is usually more informative
        // than the generic map entries.
        const code = json?.error?.code as string | undefined;
        if (code && PDF_ERROR_OVERRIDES[code]) {
          message = PDF_ERROR_OVERRIDES[code];
        } else if (json?.error?.message) {
          message = json.error.message;
        }
      }
    } catch {
      // ignore parse errors, use default message
    }
    showErrorToast(message);
  }
}

/** Format plate number with spaces between letter/digit groups: ABC1234 → ABC 1234, W1747V → W 1747 V */
export function formatPlateNumber(plate: string | null | undefined): string | null {
  if (!plate) return null;
  return plate.replace(/([A-Za-z])(\d)/g, '$1 $2').replace(/(\d)([A-Za-z])/g, '$1 $2');
}

/** Human-readable role labels */
const ROLE_LABELS: Record<string, string> = {
  SUPER_ADMIN: 'Super Admin',
  ADMIN: 'Admin',
  MANAGER: 'Manager',
  LEAD: 'Leader',
  TECH: 'Technician',
  CUSTOMER: 'Customer',
  ACCOUNTANT: 'Accountant',
};

export function formatRole(role: string): string {
  return ROLE_LABELS[role] ?? role;
}

/**
 * Title-case for display, normalising shouty/lower quote text like "COLOUR
 * ASAL" or "colour asal" → "Colour Asal". Display-only — does not change stored
 * data.
 *
 * Tokens containing a digit are treated as product codes and left verbatim, so
 * alphanumeric codes survive: "2K CLEAR" → "2K Clear", "W25", "923/255",
 * "2:1". Plain alphabetic tokens (including all-caps brand words like
 * "GLASURIT") ARE title-cased — "GLASURIT 923/255" → "Glasurit 923/255" — which
 * is the intended trade-off: normalising shouty quote entries matters more than
 * preserving the exact case of letter-only brand names.
 */
export function toTitleCase(value: string | null | undefined): string {
  if (!value) return '';
  return value
    .split(/(\s+)/)
    .map((token) =>
      /\d/.test(token) ? token : token.charAt(0).toUpperCase() + token.slice(1).toLowerCase(),
    )
    .join('');
}

/** Format elapsed duration as "Xd Yh" or "Xh Ym" or "Xm" */
export function formatElapsedDuration(startedAt: string): string {
  const ms = Date.now() - new Date(startedAt).getTime();
  const d = Math.floor(ms / 86400000);
  const h = Math.floor((ms % 86400000) / 3600000);
  const m = Math.floor((ms % 3600000) / 60000);
  if (d > 0) return `${d}d ${h}h`;
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
}

/** Format a phone number into a WhatsApp wa.me URL with Malaysian country code */
export function formatWhatsAppUrl(phone: string): string {
  const digits = phone.replace(/\D/g, '');
  const normalized = digits.startsWith('60')
    ? digits
    : digits.startsWith('0')
      ? `60${digits.slice(1)}`
      : `60${digits}`;
  return `https://wa.me/${normalized}`;
}

/** Format phone number for display: 0123456789 → 012-345 6789 */
export function formatPhoneDisplay(phone: string): string {
  const digits = phone.replace(/\D/g, '');
  const local = digits.startsWith('60')
    ? `0${digits.slice(2)}`
    : digits.startsWith('0')
      ? digits
      : `0${digits}`;
  if (local.length === 10) return `${local.slice(0, 3)}-${local.slice(3, 6)} ${local.slice(6)}`;
  if (local.length === 11) return `${local.slice(0, 3)}-${local.slice(3, 7)} ${local.slice(7)}`;
  return phone;
}

/** Extract a user-facing message from an Axios error response */
export function getApiErrorMessage(error: unknown): string {
  const apiError = (error as any)?.response?.data?.error;
  const code = apiError?.code as string | undefined;
  if (code && API_ERROR_MESSAGES[code]) return API_ERROR_MESSAGES[code];
  if (apiError?.message) return apiError.message;
  return 'Something went wrong. Please try again.';
}
